import { Component } from '@theme/component';
import { isClickedOutside } from '@theme/utilities';

/**
 * A custom element that renders the header language switcher as a listbox popup.
 * Each option is a submit button of Shopify's localization form, so picking a
 * language reloads the page in that locale.
 *
 * @typedef {object} LanguageSwitcherRefs
 * @property {HTMLFormElement} form - The localization form wrapping the trigger and panel.
 * @property {HTMLButtonElement} trigger - The button that opens the panel.
 * @property {HTMLDivElement} panel - The listbox panel.
 * @property {HTMLButtonElement[]} options - The language option buttons.
 *
 * @extends {Component<LanguageSwitcherRefs>}
 */
class LanguageSwitcherComponent extends Component {
  requiredRefs = ['form', 'trigger', 'panel', 'options'];

  get isOpen() {
    return !this.refs.panel.hidden;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleClickOutside);
  }

  /**
   * Toggles the panel.
   */
  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Opens the panel and moves focus to the current language.
   */
  open() {
    if (this.isOpen) return;

    this.refs.panel.hidden = false;
    this.refs.trigger.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', this.#handleClickOutside);

    const currentIndex = this.refs.options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
    this.#focusOption(Math.max(currentIndex, 0));
  }

  /**
   * Closes the panel.
   *
   * @param {{ returnFocus?: boolean }} [options]
   */
  close({ returnFocus = false } = {}) {
    if (!this.isOpen) return;

    this.refs.panel.hidden = true;
    this.refs.trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this.#handleClickOutside);

    if (returnFocus) this.refs.trigger.focus();
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleTriggerKeydown(event) {
    if (event.key === 'ArrowDown' && !this.isOpen) {
      event.preventDefault();
      this.open();
    } else if (event.key === 'Escape' && this.isOpen) {
      event.preventDefault();
      this.close({ returnFocus: true });
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleListKeydown(event) {
    const { options } = this.refs;
    const index = options.findIndex((option) => option === document.activeElement);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#focusOption(index + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#focusOption(index - 1);
        break;
      case 'Escape':
        event.preventDefault();
        this.close({ returnFocus: true });
        break;
      case 'Tab':
        // Move focus back to the trigger so the browser's Tab continues from there
        this.refs.trigger.focus();
        this.close();
        break;
    }
  }

  /**
   * Picking the current language only closes the panel; any other language submits the form.
   *
   * @param {MouseEvent} event
   */
  handleOptionClick(event) {
    const option = /** @type {HTMLButtonElement} */ (event.target);

    if (option.getAttribute('aria-selected') === 'true') {
      event.preventDefault();
      this.close({ returnFocus: true });
    }
  }

  /**
   * Focuses an option by index, wrapping at both ends.
   *
   * @param {number} index
   */
  #focusOption(index) {
    const { options } = this.refs;
    if (!options.length) return;

    options[(index + options.length) % options.length]?.focus();
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this.refs.form)) this.close();
  };
}

if (!customElements.get('language-switcher-component')) {
  customElements.define('language-switcher-component', LanguageSwitcherComponent);
}
